// src/composables/useNotifications.ts
import axios from "@/axiosInstance";
import { API_URL } from "@/config";
import { io, Socket } from "socket.io-client";
import { reactive, readonly } from "vue";

type Severity = "info" | "warning" | "error" | "success";

export type NotificationItem = {
	id: number;
	type?: string;
	title: string;
	admin_message?: string;
	message: string;
	severity?: Severity;
	graph_id?: number | null;
	graph_name?: string | null;
	graph_status?: string | null;
	username?: string | null;
	email?: string | null;
	// campi normalizzati:
	createdAt: string;
	is_read: boolean;
	// raw opzionali
	created_at?: string;
	read_at?: string | null;
	payload?: any;
};

const state = reactive({
	items: [] as NotificationItem[],
	unread: 0, // per il badge
	unreadCount: 0, // totale non letti lato server (serve per "Carica altre")
	ready: false,
	isAdmin: false,
	open: false,
	maxLimit: 5, // 👈 limite attuale mostrato nel pannello (usato anche dal socket)
});

let socket: Socket | null = null;

function wsUrlFromApiUrl(apiUrl?: string) {
	const base = (apiUrl || "").replace(/\/api\/?$/, "");
	return base || window.location.origin;
}

function normalize(n: any): NotificationItem {
	const createdAt =
		n.createdAt ?? n.created_at ?? n.updatedAt ?? n.updated_at ?? null;
	return {
		id: n.id,
		type: n.type,
		title: n.title,
		admin_message: n.admin_message,
		message: n.message,
		severity: n.severity,
		graph_id: n.graph_id ?? null,
		graph_name: n.graph_name ?? null,
		graph_status: n.graph_status ?? null,
		username: n.username ?? null,
		email: n.email ?? null,
		createdAt: createdAt ? String(createdAt) : new Date().toISOString(),
		is_read: !!(n.read_at ?? n.is_read),
		created_at: n.created_at,
		read_at: n.read_at,
		payload: n.payload,
	};
}

/* ------------ API ------------ */

// 👇 fetch con limite dinamico e opzioni
export async function fetchLatest(opts?: {
	limit?: number;
	scope?: "all" | "unread";
	includeRead?: boolean;
	offset?: number;
}) {
	const limit = opts?.limit ?? state.maxLimit;
	// tieni allineato lo stato del limite corrente
	state.maxLimit = limit;

	const params = {
		limit, // 👈 variabile
		scope: opts?.scope ?? "all",
		include_read: opts?.includeRead ?? true ? 1 : 0,
		offset: opts?.offset ?? 0,
	};

	const { data } = await axios.get("/notifications", { params });
	const arr = (data?.items ?? []).map(normalize);

	state.items = arr;
	state.unread = Number(
		data?.unreadCount ?? arr.filter((x: any) => !x.is_read).length,
	);
	state.unreadCount = Number(data?.unreadCount ?? state.unread); // 👈 serve per canLoadMore
}

export async function markOneAsRead(id: number) {
	await axios.put(`/notifications/${id}/read`);
	const item = state.items.find((x) => x.id === id);
	if (item && !item.is_read) {
		item.is_read = true;
		state.unread = Math.max(0, state.unread - 1);
		state.unreadCount = Math.max(0, state.unreadCount - 1);
	}
}

// (facoltativo: la tieni, ma non la userai per "carica altre")
export async function markAllAsRead() {
	await axios.post(`/notifications/read-all`);
	state.items.forEach((n) => (n.is_read = true));
	state.unread = 0;
	state.unreadCount = 0;
}

/* Permette al componente di alzare il limite prima di richiamare fetchLatest */
export function setMaxLimit(n: number) {
	state.maxLimit = Math.max(5, n);
}

/* ------------ Socket ------------ */

// function connectSocket() {
// 	if (socket) return;
// 	const WS_URL = wsUrlFromApiUrl(API_URL);

// 	socket = io(WS_URL, { withCredentials: true, transports: ["websocket"] });

// 	socket.on("notification:new", (raw: any) => {
// 		const n = normalize(raw);

// 		// evita duplicati (per id)
// 		if (!state.items.some((x) => x.id === n.id)) {
// 			state.items.unshift(n);

// 			// trim al limite CORRENTE (non più fisso a 5)
// 			if (state.items.length > state.maxLimit) state.items.length = state.maxLimit;

// 			if (!n.is_read) {
// 				state.unread = state.unread + 1;
// 				state.unreadCount = state.unreadCount + 1; // mantiene il totale allineato
// 			}
// 		}
// 	});
// }

function connectSocket() {
	if (socket) return;
	const WS_URL = wsUrlFromApiUrl(API_URL);
	socket = io(WS_URL, { withCredentials: true, transports: ["websocket"] });

	socket.on("notification:new", (raw: any) => {
		const n = normalize(raw);
		if (!state.items.some((x) => x.id === n.id)) {
			state.items.unshift(n);
			if (state.items.length > state.maxLimit)
				state.items.length = state.maxLimit;
			if (!n.is_read) {
				state.unread += 1;
				state.unreadCount += 1;
			}
		}
	});

	// ✅ patch in-place quando una notifica cambia stato (es. read_at, resolved, ecc.)
	socket.on("notification:update", (payload: any) => {
		const { id, read_at, resolved, status, graph_status } = payload;
		const it = state.items.find((x) => x.id === id);
		if (it) {
			const wasUnread = !it.is_read;

			// Update fields if present
			if (read_at !== undefined) {
				it.read_at = read_at;
				it.is_read = !!read_at;
			}
			if (resolved !== undefined) it.payload = { ...it.payload, resolved };
			if (status !== undefined) (it as any).status = status; // Cast for loose typing
			if (graph_status !== undefined) it.graph_status = graph_status;

			// Re-eval unread count ONLY if read state changed
			if (wasUnread && it.is_read) {
				state.unread = Math.max(0, state.unread - 1);
				state.unreadCount = Math.max(0, state.unreadCount - 1);
			}
			// Note: "Pulse" (unresolved count) is computed automatically from item properties
		} else {
			// fallback: puoi anche rifare un fetch se vuoi garantire coerenza
			// void fetchLatest({ limit: state.maxLimit });
		}
	});
}

/* ------------ Bootstrap ------------ */

export async function bootstrapNotifications() {
	// Lo state è un singleton: se già inizializzato, non ri-fetcha
	// (evita che il remount del NotificationBell sovrascriva il limit
	// impostato da altre pagine come AlertsPage)
	if (state.ready) return;

	try {
		const { data: me } = await axios.get("/auth/me", { withCredentials: true });
		state.isAdmin = !!me?.is_admin;
	} catch {
		state.isAdmin = false;
	}
	await fetchLatest({ limit: state.maxLimit });
	connectSocket();
	state.ready = true;
}

/* ------------ Helpers UI ------------ */
export function toggleOpen() {
	state.open = !state.open;
}
export function close() {
	state.open = false;
}


/* ------------ Composable export ------------ */
export function useNotifications() {
	return {
		state: readonly(state),
		fetchLatest,
		markOneAsRead,
		markAllAsRead,
		setMaxLimit, // 👈 utile per “Carica altre”
		toggleOpen,
		close,


	};
}
